import { startTransition, useEffect, useRef, useState } from 'react';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { toast } from '../../components/index.js';
import { getBuildInfo } from '../../lib/buildInfo.js';
import {
  annotationDisplayNumber,
  annotationSummaryText,
  getDisplayOrderedAnnotations,
} from './feedbackAnnotations.js';
import { useFeedbackMarkup } from './FeedbackMarkupProvider.jsx';
import { getFeedbackPackage, submitFeedbackPackage } from './feedbackApi.js';
import {
  clearPendingFeedbackMutation,
  feedbackMutationSignature,
  pendingFeedbackMutation,
  shouldRetainFeedbackMutation,
} from './feedbackIdempotency.js';
import { annotationKindLabel, colorLabelForValue } from './feedbackTypes.js';

function annotationHasNote(annotation) {
  return Boolean(
    String(annotation?.instruction || '').trim() ||
    String(annotation?.feedbackDraft?.description || '').trim() ||
    String(annotation?.feedbackDraft?.title || '').trim(),
  );
}

export function FeedbackReviewModal() {
  const {
    state,
    dispatch,
    saveDraft,
    exportPackage,
    jumpToAnnotationFromReview,
    completeSubmittedSession,
    navigate,
  } = useFeedbackMarkup();
  const modalRef = useRef(null);
  const returnRef = useRef(null);
  const [submitState, setSubmitState] = useState({ status: 'idle', message: '' });
  const [notelessWarned, setNotelessWarned] = useState(false);
  const { user } = useCurrentUser();
  const pkg = state.package;

  useEffect(() => {
    // Re-warn on the next review session — the soft confirm is per-open.
    if (!state.reviewOpen) setNotelessWarned(false);
  }, [state.reviewOpen]);

  useEffect(() => {
    // Escape must not close the modal mid-submit: the submit keeps running
    // and would later yank the user to the confirmation page from wherever
    // they navigated. Capture-phase so the provider's close handler never
    // sees the event.
    if (submitState.status !== 'submitting') return undefined;
    const blockEscape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockEscape, true);
    return () => window.removeEventListener('keydown', blockEscape, true);
  }, [submitState.status]);

  useEffect(() => {
    if (!state.reviewOpen) return undefined;
    returnRef.current = document.activeElement;
    const reviewBody = modalRef.current?.querySelector('.feedback-review-body');
    if (reviewBody && Number.isFinite(Number(state.reviewReturnContext?.scrollTop))) {
      reviewBody.scrollTop = Number(state.reviewReturnContext.scrollTop);
    }
    const first = modalRef.current?.querySelector('input, textarea, button, select');
    first?.focus?.();
    const onKeyDown = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) || [],
      ).filter((item) => !item.disabled);
      if (focusable.length === 0) return;
      const firstItem = focusable[0];
      const lastItem = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      returnRef.current?.focus?.();
    };
  }, [state.reviewOpen, state.reviewReturnContext?.scrollTop]);

  if (!state.reviewOpen || !pkg) return null;
  const screens = pkg.screens || [];
  const annotationCount = screens.reduce(
    (sum, screen) => sum + (screen.metadata?.annotations?.length || 0),
    0,
  );
  const notelessCount = screens.reduce(
    (sum, screen) =>
      sum +
      (screen.metadata?.annotations || []).filter((annotation) => !annotationHasNote(annotation))
        .length,
    0,
  );
  const buildInfo = getBuildInfo();
  const environmentHost = typeof window !== 'undefined' ? window.location.host : null;

  function reviewPill(annotation) {
    const severity = annotation?.feedbackDraft?.severity;
    if (severity === 'critical') return 'Blocking';
    if (severity === 'high') return 'Important';
    if (severity === 'medium') return 'Should fix';
    if (severity === 'low') return 'Nice to have';
    return null;
  }

  async function submitToQueue() {
    // Soft confirm, never a block: a marked screenshot without a note is still
    // useful evidence, but a sentence helps the team act (prototype AC-g).
    if (notelessCount > 0 && !notelessWarned) {
      setNotelessWarned(true);
      return;
    }
    setSubmitState({ status: 'submitting', message: 'Submitting governed feedback records...' });
    let submitIdentity = null;
    try {
      const saved = await saveDraft();
      if (!saved?.id || !Number.isInteger(saved?.revision)) {
        // A failed pre-submit save has exactly one benign cause: the submit
        // already committed server-side and the response was lost, making the
        // package immutable. Check the server before declaring failure —
        // otherwise the tester is stuck in an un-savable, un-discardable
        // session with a retained idempotency key that never gets resent.
        const serverPackage =
          pkg?.id && !String(pkg.id).startsWith('FP-LOCAL')
            ? await getFeedbackPackage(pkg.id)
                .then((response) => response?.data || null)
                .catch(() => null)
            : null;
        if (serverPackage?.status === 'submitted') {
          clearPendingFeedbackMutation({
            actorUserId: user?.id,
            operation: 'package-submit',
            resourceId: pkg.id,
          });
          setSubmitState({ status: 'submitted', message: 'This package was already submitted.' });
          toast.success({
            title: 'Feedback already submitted',
            body: 'This package is already with the product team.',
          });
          navigate(`/feedback/submitted/${pkg.id}`, {
            state: {
              title: serverPackage.title || pkg.title,
              recordIds: serverPackage.submittedFeedbackIds || [],
              sourceRoute: screens[0]?.metadata?.route || '/home',
            },
          });
          // INV-02: the clears ride the SAME transition lane as the
          // navigation (v7_startTransition wraps navigate's location update
          // in a transition) so both commit atomically — a sync-priority
          // clear would re-render the still-mounted workspace route
          // stage-less first, and its guard's replace-to-/feedback would
          // clobber the pending confirmation navigation.
          startTransition(() => {
            dispatch({ type: 'package/set', package: serverPackage });
            dispatch({ type: 'stage/discard' });
            dispatch({ type: 'review/close' });
            completeSubmittedSession();
          });
          return;
        }
        setSubmitState({
          status: 'failed',
          message: 'Save the draft successfully before submitting feedback to the queue.',
        });
        return;
      }
      const mutationIdentity = {
        actorUserId: user?.id,
        operation: 'package-submit',
        resourceId: saved.id,
      };
      submitIdentity = mutationIdentity;
      const pending = pendingFeedbackMutation({
        ...mutationIdentity,
        signature: feedbackMutationSignature({
          packageId: saved.id,
          expectedRevision: saved.revision,
        }),
      });
      const response = await submitFeedbackPackage(saved.id, {
        expectedRevision: saved.revision,
        idempotencyKey: pending.key,
      });
      clearPendingFeedbackMutation(mutationIdentity);
      const feedbackRecords =
        response?.data?.data?.feedbackRecords ??
        response?.data?.feedbackRecords ??
        response?.feedbackRecords ??
        [];
      const submittedCount = feedbackRecords.length;
      setSubmitState({
        status: 'submitted',
        message: `Submitted ${submittedCount || annotationCount} feedback record${submittedCount === 1 || annotationCount === 1 ? '' : 's'}.`,
      });
      // Reflect the submitted state locally: the package flips to submitted
      // (badge clears, session bar hides, My feedback files it under Sent)
      // and the editing stage closes.
      const submittedPackage =
        response?.data?.data?.package ?? response?.data?.package ?? response?.package ?? null;
      // W3A: submitting ends on the confirmation page (reference IDs + a
      // tracking link), never on the triager's queue. INV-02 (live
      // 2026-07-23): with v7_startTransition, navigate's location update is
      // transition-priority — sync-priority state clears here would commit
      // FIRST, the still-mounted workspace route would render stage-less at
      // the old location, and its guard's replace-to-/feedback would clobber
      // the pending confirmation navigation. The clears must ride the same
      // transition lane so location change and session teardown commit in
      // one render.
      navigate(`/feedback/submitted/${saved.id}`, {
        state: {
          title: pkg.title,
          recordIds: feedbackRecords.map((record) => record.id),
          sourceRoute: screens[0]?.metadata?.route || '/home',
        },
      });
      startTransition(() => {
        if (submittedPackage) {
          dispatch({ type: 'package/set', package: submittedPackage });
        }
        dispatch({ type: 'stage/discard' });
        dispatch({ type: 'review/close' });
        completeSubmittedSession();
      });
      // BL-06 (Hugh Burt, TC-PMG8-FEEDBACK-002B): a visible success cue plus
      // the confirmation page — submitted work is tracked under Submitted,
      // never Drafts.
      toast.success({
        title: 'Feedback submitted',
        body: 'Your package is tracked under Submitted in My feedback.',
      });
    } catch (error) {
      // Use the identity captured AFTER the pre-submit save: a local-only
      // package is promoted to its server id during that save, and clearing
      // under the stale render-time id would leak the retained key.
      const mutationIdentity = submitIdentity || {
        actorUserId: user?.id,
        operation: 'package-submit',
        resourceId: pkg?.id,
      };
      if (!shouldRetainFeedbackMutation(error)) clearPendingFeedbackMutation(mutationIdentity);
      if (
        (error?.code === 'CONFLICT' && error?.details?.reason === 'already_submitted') ||
        error?.code === 'FEEDBACK_PACKAGE_ALREADY_SUBMITTED'
      ) {
        setSubmitState({
          status: 'submitted',
          message: 'This package was already submitted.',
        });
        toast.success({
          title: 'Feedback already submitted',
          body: 'This package is already with the product team.',
        });
        // INV-02: same rule as the happy path — navigate first, clears in
        // the same transition lane as the location update.
        navigate(`/feedback/submitted/${pkg.id}`, {
          state: { title: pkg.title, sourceRoute: screens[0]?.metadata?.route || '/home' },
        });
        startTransition(() => {
          dispatch({
            type: 'package/update',
            patch: {
              status: 'submitted',
              submittedAt: pkg.submittedAt || new Date().toISOString(),
            },
          });
          dispatch({ type: 'stage/discard' });
          dispatch({ type: 'review/close' });
          completeSubmittedSession();
        });
        return;
      }
      const baseMessage = error?.message || 'Could not submit feedback to the governed queue.';
      // The API sanitizes unexpected 500s to safe copy; append the request id
      // so a tester can quote a support reference without reading logs.
      const message = error?.requestId
        ? `${baseMessage} (reference ${error.requestId})`
        : baseMessage;
      setSubmitState({ status: 'failed', message });
      toast.error({ title: 'Feedback submission failed', body: message });
    }
  }

  return (
    <div className="feedback-modal-backdrop" role="presentation">
      <section
        className="feedback-review-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Review feedback package"
        data-testid="feedback-review-modal"
        ref={modalRef}
      >
        <header>
          <div>
            <div className="feedback-panel-kicker">Feedback</div>
            <h2>Review feedback package</h2>
          </div>
          <button
            type="button"
            className="btn ghost sm"
            data-testid="feedback-review-close"
            onClick={() => dispatch({ type: 'review/close' })}
          >
            Close
          </button>
        </header>
        <div className="feedback-review-body" data-testid="feedback-review-body">
          <label className="feedback-field">
            <span>Package title</span>
            <input
              data-testid="feedback-review-title"
              maxLength={400}
              value={pkg.title || ''}
              onChange={(event) =>
                dispatch({ type: 'package/update', patch: { title: event.target.value } })
              }
            />
          </label>
          <div className="feedback-review-context" data-testid="feedback-review-context">
            <div className="feedback-review-context-title">
              Captured context — attached automatically
            </div>
            <div>
              {[
                [pkg.createdBy?.displayName, pkg.createdBy?.persona].filter(Boolean).join(' · ') ||
                  'Signed-in user',
                environmentHost,
                buildInfo?.version ? `Build ${buildInfo.version}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
            {screens.map((screen, index) => {
              const metadata = screen.metadata || {};
              const viewport = metadata.viewport || null;
              return (
                <div key={screen.screenId || index}>
                  {[
                    screen.screenId || metadata.screenId,
                    metadata.pageTitle,
                    metadata.route,
                    viewport ? `${viewport.width}×${viewport.height}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              );
            })}
          </div>
          <label className="feedback-field">
            <span>Overall instructions</span>
            <textarea
              data-testid="feedback-review-overall-instructions"
              aria-label="Overall instructions"
              value={pkg.overallInstructions || ''}
              onChange={(event) =>
                dispatch({
                  type: 'package/update',
                  patch: { overallInstructions: event.target.value },
                })
              }
              rows={4}
            />
          </label>
          {screens.length === 0 ? (
            <div className="feedback-empty">
              No screens captured yet. Capture the visible viewport or select a region to begin.
            </div>
          ) : (
            <ol className="feedback-review-screens">
              {screens.map((screen, index) => (
                <li key={screen.screenId || index}>
                  <img src={screen.screenshotDataUrl} alt="" />
                  <div>
                    <strong>{screen.metadata?.pageTitle || screen.metadata?.route}</strong>
                    <span>{screen.metadata?.route}</span>
                    <ol className="feedback-review-annotations">
                      {getDisplayOrderedAnnotations(screen.metadata?.annotations || []).map(
                        (annotation) => (
                          <li key={annotation.id} className="feedback-review-annotation-summary">
                            <div>
                              <strong>
                                <span
                                  className="feedback-review-swatch"
                                  style={{
                                    background: annotation?.style?.strokeColor || '#F4B400',
                                  }}
                                  aria-hidden="true"
                                />
                                {`Mark ${annotationDisplayNumber(annotation.id)} · ${annotationKindLabel(annotation?.type)}`}
                              </strong>
                              <p>{annotationSummaryText(annotation)}</p>
                              <div className="feedback-review-summary-pills">
                                <span>{colorLabelForValue(annotation?.style?.strokeColor)}</span>
                                {reviewPill(annotation) ? (
                                  <span>{reviewPill(annotation)}</span>
                                ) : null}
                                {annotation?.feedbackDraft?.actualBehavior ||
                                annotation?.feedbackDraft?.expectedBehavior ? (
                                  <span>More detail added</span>
                                ) : null}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn ghost sm"
                              data-testid={`feedback-review-edit-${
                                screen.screenId || screen.metadata?.screenId
                              }-${annotation.id}`}
                              onClick={() =>
                                jumpToAnnotationFromReview?.(
                                  screen.screenId || screen.metadata?.screenId,
                                  annotation.id,
                                )
                              }
                            >
                              {`Edit mark ${annotationDisplayNumber(annotation.id)}`}
                            </button>
                          </li>
                        ),
                      )}
                    </ol>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
        <footer>
          {notelessWarned && notelessCount > 0 ? (
            <span
              className="feedback-review-noteless-warning"
              data-testid="feedback-review-noteless-warning"
              aria-live="polite"
            >
              {notelessCount === 1
                ? '1 mark has no note — the marked screenshot still goes, but a sentence helps the team act.'
                : `${notelessCount} marks have no note — the marked screenshots still go, but a sentence helps the team act.`}
            </span>
          ) : null}
          {submitState.message ? (
            <span className={`feedback-save-status ${submitState.status}`} aria-live="polite">
              {submitState.message}
            </span>
          ) : null}
          {state.exportMessage ? (
            <span className={`feedback-save-status ${state.exportStatus}`} aria-live="polite">
              {state.exportMessage}
            </span>
          ) : null}
          <button
            type="button"
            className="btn ghost"
            data-testid="feedback-review-save-draft"
            onClick={saveDraft}
            disabled={submitState.status === 'submitting'}
          >
            Save draft
          </button>
          <button
            type="button"
            className="btn outline"
            data-testid="feedback-review-submit"
            onClick={submitToQueue}
            disabled={
              screens.length === 0 || annotationCount === 0 || submitState.status === 'submitting'
            }
          >
            {submitState.status === 'submitting'
              ? 'Submitting to queue...'
              : notelessWarned && notelessCount > 0
                ? 'Send anyway'
                : 'Submit to feedback queue'}
          </button>
          <button
            type="button"
            className="btn ghost"
            data-testid="feedback-export-draft-zip"
            onClick={(event) =>
              exportPackage({ final: false, returnFocus: event.currentTarget })
            }
            disabled={
              screens.length === 0 ||
              state.exportStatus === 'exporting' ||
              submitState.status === 'submitting'
            }
          >
            Export draft ZIP
          </button>
          <button
            type="button"
            className="btn primary"
            data-testid="feedback-export-zip"
            onClick={(event) =>
              exportPackage({ final: true, returnFocus: event.currentTarget })
            }
            disabled={
              screens.length === 0 ||
              state.exportStatus === 'exporting' ||
              submitState.status === 'submitting'
            }
          >
            {state.exportStatus === 'exporting' ? 'Exporting ZIP...' : 'Export ZIP'}
          </button>
        </footer>
      </section>
    </div>
  );
}
